import React, {useContext} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import BoFormOccurrenceData from "./components/bo-form-occurrence-data.jsx";
import {useLoaderData} from "react-router";
import {BoFormContext} from "./context/bo-form-context.jsx";
import {FormProvider, useForm} from "react-hook-form";
import BoFormIndividual from "./components/bo-form-individual.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import BoFormCompany from "./components/bo-form-company.jsx";
import BoFormApprehension from "./components/bo-form-apprehension.jsx";
import BoFormStolenItem from "./components/bo-form-stolen-item.jsx";
import BoFormVehicle from "./components/bo-form-vehicle.jsx";
import BoFormCroqui from "./components/bo-form-croqui.jsx";
import BoFormOtherDocuments from "./components/bo-form-other-documents.jsx";
import BoFormExtraInfo from "./components/bo-form-extra-info.jsx";
import BoFormGcmSignature from "./components/bo-form-gcm-signature.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import bosService from "../../../../services/web/bos-service.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {format, isValid} from "date-fns";
import {normalizeCoordinate} from "../../individual/form/resources/map-utils.js";

const BoForm = () => {
    const {id, boData, isLoading, isLoadingFetch, setIsLoading, dataFetch, objectTypes, gedAttachIds, syncId} = useContext(BoFormContext);
    const navigate = navigateHook();

    const status = dataFetch?.bogcm?.bogcm?.status;

    const methods = useForm({
        values: boData,
        defaultValues: {
            stolenItemList: [],
            individualList: [],
            apprehensionList: [],
            vehicleList: [],
            companyList: [],
            otherDocumentsList: [],
            occurrenceData: { reason: [] },
            croqui: { attachs: [] },
        },
        resetOptions: { keepDefaultValues: true }
    });

    const loaderData = useLoaderData();

    const validate = (data) => {
        const occ = data?.occurrenceData;
        const addressFields = ["address", "number", "neighborhood", "city", "zipCode", "complement", "lat", "lng"];
        const hasValue = (value) => value !== null && value !== undefined && (typeof value !== "string" || value.trim() !== "");
        const addressHasData = (address) => addressFields.some((field) => hasValue(address?.[field]));
        const addressesAreEqual = (first, second) => addressFields.every((field) => (first?.[field] ?? null) === (second?.[field] ?? null));

        if (!occ?.reason?.length) {
            projectUtils.swalErrorMessage('', 'A seção "Natureza" é obrigatória.', 'Ok');
            return false;
        }

        if (occ?.reason?.some(r => !r.name || r.name === "")) {
            projectUtils.swalErrorMessage('', 'O campo "Natureza" (Naturezas) é obrigatório.', 'Ok');
            return false;
        }

        if (!occ?.dtFact) {
            projectUtils.swalErrorMessage('', 'O campo "D/H do fato" (Dados de ocorrência) é obrigatório.', 'Ok');
            return false;
        }

        if (!occ?.dtEnd) {
            projectUtils.swalErrorMessage('', 'O campo "D/H final" (Dados de ocorrência) é obrigatório.', 'Ok');
            return false;
        }

        if (occ?.flagrant === undefined || occ?.flagrant === null) {
            projectUtils.swalErrorMessage('', 'O campo "Flagrante" (Dados de ocorrência) é obrigatório.', 'Ok');
            return false;
        }

        if (!occ?.address?.neighborhood) {
            projectUtils.swalErrorMessage('', 'O campo "Bairro" (Dados de ocorrência) é obrigatório.', 'Ok');
            return false;
        }

        if (!data?.gcmReport) {
            projectUtils.swalErrorMessage('', 'O campo "Descrição" (Relatório do agente) é obrigatório.', 'Ok');
            return false;
        }

        if (data?.individualList?.length) {
            for (const individual of data.individualList) {
                const initialIndividual = boData?.individualList?.find((item) => item.itemId === individual.itemId);
                const unchangedLegacyAddress = initialIndividual
                    && addressHasData(initialIndividual.address)
                    && !initialIndividual.address?.date
                    && addressesAreEqual(initialIndividual.address, individual.address);

                if (addressHasData(individual.address) && !individual.address?.date && !unchangedLegacyAddress) {
                    projectUtils.swalErrorMessage('', 'O campo "Data" (Endereço do indivíduo) é obrigatório.', 'Ok');
                    return false;
                }

                if (!individual.conditions?.length) {
                    projectUtils.swalErrorMessage('', 'O campo "Condições" (Indivíduos) é obrigatório.', 'Ok');
                    return false;
                }
                if (!individual.referral?.length) {
                    projectUtils.swalErrorMessage('', 'O campo "Encaminhamentos" (Indivíduos) é obrigatório.', 'Ok');
                    return false;
                }

                const version = individual.individualVersion?.[individual.individualVersion.length - 1];
                if (!version || (!version.nothingToDeclare && !version.signOption && !version.version)) {
                    projectUtils.swalErrorMessage('', 'O campo "Versão do indivíduo" (Indivíduos) é obrigatório.', 'Ok');
                    return false;
                }
            }
        }

        const validateItemType = (item, sectionName) => {
            if (!item.type) {
                projectUtils.swalErrorMessage('', `O campo "Tipo" (${sectionName}) é obrigatório.`, 'Ok');
                return false;
            }

            const objType = objectTypes.find(o => o.name === item.type);
            if (objType) {
                const missingFields = [];

                if (objType.mandatorySubtype && !item.subtype) missingFields.push('"Subtipo"');
                if (objType.mandatoryQuantity) {
                    if (!item.quantity) missingFields.push('"Quantidade"');
                    if (!item.unit) missingFields.push('"Unidade"');
                }

                if (missingFields.length > 0) {
                    const message = `O campo ${missingFields[0]} (${sectionName}) é obrigatório.`;
                    projectUtils.swalErrorMessage('', message, 'Ok');
                    return false;
                }
            }
            return true;
        };

        if (data?.apprehensionList?.length) {
            for (const apprehension of data.apprehensionList) {
                if (!validateItemType(apprehension, 'Apreensões')) return false;
            }
        }

        if (data?.stolenItemList?.length) {
            for (const item of data.stolenItemList) {
                if (!validateItemType(item, 'Itens subtraídos')) return false;
            }
        }

        if (data?.vehicleList?.length) {
            const currentYear = new Date().getFullYear();
            const minimumYear = 1800;

            for (const vehicle of data.vehicleList) {
                if (vehicle.fabricationYear) {
                    if (vehicle.fabricationYear > currentYear) {
                        projectUtils.swalErrorMessage('', `O campo "Ano de fabricação" (Veículos) não pode ser maior que o ano atual (${currentYear}).`, 'Ok');
                        return false;
                    }
                    if (vehicle.modelYear && vehicle.fabricationYear > vehicle.modelYear) {
                        projectUtils.swalErrorMessage('', 'O campo "Ano de fabricação" (Veículos) não pode ser maior que o campo Ano do modelo.', 'Ok');
                        return false;
                    }
                    if (vehicle.fabricationYear < minimumYear) {
                        projectUtils.swalErrorMessage('', `O campo "Ano de fabricação" (Veículos) não pode ser inferior a (${minimumYear}).`, 'Ok');
                        return false;
                    }
                }

                if (vehicle.modelYear && vehicle.modelYear > currentYear + 1) {
                    projectUtils.swalErrorMessage('', `O campo "Ano do modelo" (Veículos) não pode ser maior que o próximo ano (${currentYear + 1}).`, 'Ok');
                    return false;
                }

                const hasAnyVehicleField = vehicle.brandModel || vehicle.chassis || vehicle.city ||
                    vehicle.color || vehicle.plate || vehicle.relatedPerson || vehicle.renavam || vehicle.type ||
                    vehicle.vehicleApprehensionDescription || vehicle.vehicleApprehensionDestiny ||
                    vehicle.vehicleApprehensionReceiver || vehicle.year;

                if (!hasAnyVehicleField) {
                    projectUtils.swalErrorMessage('', 'Nenhum campo foi preenchido em veículo.', 'Ok');
                    return false;
                }
            }
        }

        if (data?.otherDocumentsList?.some(i => !i.type)) {
            projectUtils.swalErrorMessage('', 'O campo "Tipo" (Documentos) é obrigatório.', 'Ok');
            return false;
        }

        return true;
    };

    const prepareData = (formData, dataRaw, syncAttachs) => {
        const toLocalDate = (date, type = "dd/MM/yyyy HH:mm") => {
            if (!date) return null;

            if (date instanceof Date) return isValid(date) ? format(date, type) : null;

            return date;
        }

        const replaceEmptyStringsWithNull = (val) => {
            if (val === "") return null;
            if (typeof val !== "object" || val === null) return val;

            if (Array.isArray(val)) {
                return val.map(replaceEmptyStringsWithNull);
            }

            const cleanedObj = {};
            for (const key in val) {
                if (key === "gcmReportSignature") return val;
                cleanedObj[key] = replaceEmptyStringsWithNull(val[key]);
            }
            return cleanedObj;
        };

        const getExtensionFromFile = (file) => file.name.split(/[\\/]/).pop().split('.').pop()

        const data = {
            id: id,
            occurrenceData: {
                ...dataRaw?.occurrenceData,
                dtFact: toLocalDate(dataRaw?.occurrenceData?.dtFact, dataRaw?.occurrenceData?.dtFactUncertain ? "dd/MM/yyyy" : "dd/MM/yyyy HH:mm"),
                dtPlace: toLocalDate(dataRaw?.occurrenceData?.dtPlace),
                dtEnd: toLocalDate(dataRaw?.occurrenceData?.dtEnd),
            },
            initialData: {
                creationDate: dataRaw?.initialData?.creationDate ?? null,
                garrisonPrefix: dataRaw?.initialData?.garrisonPrefix ?? null,
                occurrenceNumber: dataRaw?.initialData?.occurrenceNumber ?? null
            },
            stolenItemList: dataRaw?.stolenItemList ?? [],
            gcmReport: dataRaw?.gcmReport,
            extraInfo: dataRaw?.extraInfo,
            source: 'WEB',
        };

        if (dataRaw?.gcmReportSignature) {
            const { file, ...cleanSignature } = dataRaw.gcmReportSignature;
            data.gcmReportSignature = cleanSignature;

            if (file) {
                formData.append("gcm_signature", file);
            }
        }

        data.companyList = (dataRaw?.companyList ?? []).map((item, i) => {
            const newItem = { ...item };
            newItem.cnpj = newItem.cnpj?.replace(/[^a-zA-Z0-9]/g, '')

            return newItem;
        });

        data.individualList = (dataRaw?.individualList ?? []).map((item, i) => {
            const newItem = { ...item };
            newItem.cpf = newItem.cpf?.replaceAll('.', '')?.replace('-', '');
            newItem.dtBirth = toLocalDate(newItem.dtBirth,  "dd/MM/yyyy");
            newItem.medicalExamDueDate = toLocalDate(newItem.medicalExamDueDate,  "dd/MM/yyyy");

            if (newItem.address) {
                newItem.address = {
                    ...newItem.address,
                    lat: normalizeCoordinate(newItem.address.lat),
                    lng: normalizeCoordinate(newItem.address.lng),
                };
            }

            newItem.attachs = (newItem.attachs ?? []).map((attach, j) => {
                if (attach?.file) {
                    syncAttachs.push({attachId: attach.attachId, attachFile: attach.file});
                    formData.append(`individual_${i}_${j}`, attach.file);

                    const { file, ...cleanAttach } = attach;
                    return { ...cleanAttach, extension: getExtensionFromFile(file) };
                }
                return attach;
            });

            return newItem;
        });

        data.apprehensionList = (dataRaw?.apprehensionList ?? []).map((item, i) => {
            const newItem = { ...item };
            newItem.attachs = (newItem.attachs ?? []).map((attach, j) => {
                if (attach?.file) {
                    syncAttachs.push({attachId: attach.attachId, attachFile: attach.file});
                    formData.append(`apprehension_${i}_${j}`, attach.file);

                    const { file, ...cleanAttach } = attach;
                    return { ...cleanAttach, extension: getExtensionFromFile(file) };
                }
                return attach;
            });
            return newItem;
        });

        data.stolenItemList = (dataRaw?.stolenItemList ?? []).map((item, i) => {
            const newItem = { ...item };
            newItem.attachs = (newItem.attachs ?? []).map((attach, j) => {
                if (attach?.file) {
                    syncAttachs.push({attachId: attach.attachId, attachFile: attach.file});
                    formData.append(`stolen_${i}_${j}`, attach.file);

                    const { file, ...cleanAttach } = attach;
                    return { ...cleanAttach, extension: getExtensionFromFile(file) };
                }
                return attach;
            });
            return newItem;
        });

        data.vehicleList = (dataRaw?.vehicleList ?? []).map((item, i) => {
            const newItem = { ...item };
            newItem.plate = newItem.plate?.replace('-', '');

            newItem.attachs = (newItem.attachs ?? []).map((attach, j) => {
                if (attach?.file) {
                    syncAttachs.push({attachId: attach.attachId, attachFile: attach.file});
                    formData.append(`vehicle_${i}_${j}`, attach.file);

                    const { file, ...cleanAttach } = attach;
                    return { ...cleanAttach, extension: getExtensionFromFile(file) };
                }
                return attach;
            });
            return newItem;
        });

        data.croqui = {
            observation: dataRaw?.croqui?.observation,
            attachs: (dataRaw?.croqui?.attachs ?? []).map((attach, i) => {
                if (attach?.file) {
                    syncAttachs.push({attachId: attach.attachId, attachFile: attach.file});
                    formData.append(`croqui_${i}`, attach.file);

                    const { file, ...cleanAttach } = attach;
                    return { ...cleanAttach, extension: getExtensionFromFile(file) };
                }
                return attach;
            })
        };

        data.otherDocumentsList = (dataRaw?.otherDocumentsList ?? []).map((item, i) => {
            const newItem = { ...item };
            newItem.attachs = (newItem.attachs ?? []).map((attach, j) => {
                if (attach?.file) {
                    syncAttachs.push({attachId: attach.attachId, attachFile: attach.file});
                    formData.append(`otherdocuments_${i}_${j}`, attach.file);

                    const { file, ...cleanAttach } = attach;
                    return { ...cleanAttach, extension: getExtensionFromFile(file) };
                }
                return attach;
            });
            return newItem;
        });

        const body = {
            id,
            data: replaceEmptyStringsWithNull(data),
            gedAttachIds,
        };

        formData.append('bogcm', JSON.stringify(body));
    }

    const submit = () => {
        setIsLoading(true);

        const dataRaw = methods.getValues();
        if (!validate(dataRaw)) {
            setIsLoading(false)

            return;
        }

        const formData = new FormData();

        const syncAttachs = [];

        try {
            prepareData(formData, dataRaw, syncAttachs)
        } catch (e) {
            console.error(e);
            setIsLoading(false)
            projectUtils.swalErrorMessage('', "Não foi possível salvar.", 'Ok');

            return
        }

        switch (status) {
            case "REJECTED":
                updateBo(formData)
                break;
            case "NUMBER_GENERATED":
                syncStart(formData, syncAttachs)
                break;
            case "DRAFT":
                startBasedOnExisting(formData, syncAttachs)
                break;
        }
    }

    const updateBo = (formData) => {
        bosService.update(formData)
            .then(() => {
                const boId = id.replace("/","-");

                projectUtils.success(true);
                navigate(`/bo/show/${boId}`);
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';

                setIsLoading(false);
                projectUtils.swalErrorMessage('', message, 'Ok');
            });

    }

    const syncStart = (formData, syncAttachs) => {
        const formDataStartSync = new FormData();
        formDataStartSync.append('bogcmId', id);

        bosService.syncStart(formDataStartSync)
            .then((data) => {
                formData.append('syncId', data.id);
                formData.append('deviceTimestamp', Date.now());

                syncData(formData, syncAttachs, data.id)
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';

                setIsLoading(false);
                projectUtils.swalErrorMessage('', message, 'Ok');
            });

    }

    const startBasedOnExisting = (formData, syncAttachs) => {
        const formDataStartSync = new FormData();
        formDataStartSync.append('existing', syncId);

        bosService.startBasedOnExisting(formDataStartSync)
            .then((data) => {
                formData.append('syncId', data.syncId);
                formData.append('deviceTimestamp', Date.now());

                syncData(formData, syncAttachs, data.syncId)
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';

                setIsLoading(false);
                projectUtils.swalErrorMessage('', message, 'Ok');
            });

    }

    const syncData = (formData, syncAttachs, syncId) => {
        bosService.syncData(formData)
            .then(async () => {

                if (syncAttachs.length > 0) {

                    let allAttachSync = 0;

                    for (let i = 0; i < syncAttachs.length; i++) {

                        let attach = syncAttachs[i];
                        const formDataAttachSync = new FormData();
                        formDataAttachSync.append('syncId', syncId);
                        formDataAttachSync.append('attachId', attach.attachId.toString());
                        formDataAttachSync.append('file', attach.attachFile);

                        await bosService.syncAttach(formDataAttachSync)
                            .then(() => {
                                allAttachSync ++;
                            })
                            .catch(error => {
                                console.error(error);
                                let message = 'Não foi possível salvar.';

                                setIsLoading(false);
                                projectUtils.swalErrorMessage('', message, 'Ok');
                            });
                    }

                    if (allAttachSync === syncAttachs.length) {
                        syncDataSetFullySynced(syncId);
                    }

                } else {
                    syncDataSetFullySynced(syncId);
                }


            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';

                setIsLoading(false);
                projectUtils.swalErrorMessage('', message, 'Ok');
            });

    }

    const syncDataSetFullySynced = (syncId) => {
        const formDataEndSync = new FormData();
        formDataEndSync.append('syncId', syncId);
        formDataEndSync.append('deviceTimestamp', Date.now());

        bosService.fullySynced(formDataEndSync)
            .then(() => {
                const boId = id.replace("/","-");

                projectUtils.success(true);
                navigate(`/bo/show/${boId}`);
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';

               setIsLoading(false);
                projectUtils.swalErrorMessage('', message, 'Ok');
            });
    }

    return (
        <Template isLoading={isLoadingFetch || isLoading}>
            <Template.Header>
                <Template.Title>{loaderData?.bo_name} {id}</Template.Title>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <FormProvider {...methods}>
                    <BoFormOccurrenceData />

                    <BoFormIndividual />

                    <BoFormCompany />

                    <BoFormApprehension />

                    <BoFormStolenItem />

                    <BoFormVehicle />

                    <BoFormCroqui />

                    <BoFormOtherDocuments />

                    <BoFormExtraInfo />

                    <BoFormGcmSignature />
                </FormProvider>

                <Card>
                    <Card.Body>
                        <Button className={"justify-self-end"} onClick={submit} actionFeedbackType='save'>
                            {status === "REJECTED" ? "Salvar e enviar para aprovação" : "Salvar"}
                        </Button>
                    </Card.Body>
                </Card>

            </Template.Body>
        </Template>
    );
};

export default BoForm;
